import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp, TrendingDown, BarChart3, LineChart, Activity, DollarSign,
  Users, Target, ArrowUpRight, ArrowDownRight, Minus, Info, RefreshCw,
  Calendar, Filter, Download, ChevronDown, ChevronUp, Eye, AlertCircle, X,
  MapPin, Layers, GitBranch, Zap, PieChart, Search, ArrowRight,
  Database, Globe, FileText, Upload, CheckCircle, Clock, AlertTriangle, Hash
} from 'lucide-react';
import { apiCallJson } from './Api';

// Safe formatting helpers — prevent $undefined / NaN
const fmt = (v, fb = '0') =>
  v == null || v === '' || Number.isNaN(v) ? fb : typeof v === 'number' ? v.toLocaleString() : String(v);
const fmtDec = (v, d = 1, fb = '0') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb : Number(v).toFixed(d);
const fmtMoney = (v, d = 0, fb = '$0') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb
    : '$' + (d > 0 ? Number(v).toFixed(d) : Number(v).toLocaleString());
const fmtPct = (v, d = 1, fb = '0%') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb : Number(v).toFixed(d) + '%';
const fmtSign = (v, d = null, fb = '0') => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return fb;
  const n = Number(v);
  return (n >= 0 ? '+' : '') + (d !== null ? n.toFixed(d) : n.toLocaleString());
};
const fmtBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
};

const CDPAnalyticsTab = ({ sessionId, currentVersion, user }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedVersionFrom, setSelectedVersionFrom] = useState(null);
  const [selectedVersionTo, setSelectedVersionTo] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [comparisonData, setComparisonData] = useState(null);
  const [waterfallData, setWaterfallData] = useState(null);
  const [fieldChanges, setFieldChanges] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignsSummary, setCampaignsSummary] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerJourney, setCustomerJourney] = useState(null);
  const [journeyPatterns, setJourneyPatterns] = useState(null);
  const [journeySegments, setJourneySegments] = useState(null);
  const [fieldEvolution, setFieldEvolution] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [segmentMigrations, setSegmentMigrations] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');

  useEffect(() => {
    loadDashboard();
  }, [sessionId]);

  useEffect(() => {
    if (selectedVersionFrom && selectedVersionTo) {
      loadComparison();
    }
  }, [selectedVersionFrom, selectedVersionTo]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/dashboard`);
      setDashboardData(response);
      
      if (response.session.current_version > 1) {
        setSelectedVersionFrom(response.session.current_version - 1);
        setSelectedVersionTo(response.session.current_version);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  const loadComparison = async () => {
    if (selectedVersionFrom && selectedVersionTo) {
      try {
        const [comparison, waterfall, segMigrations] = await Promise.all([
          apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/version-comparison?from_version=${selectedVersionFrom}&to_version=${selectedVersionTo}`),
          apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/waterfall?from_version=${selectedVersionFrom}&to_version=${selectedVersionTo}`),
          apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/segment-migrations?from_version=${selectedVersionFrom}&to_version=${selectedVersionTo}`).catch(() => null)
        ]);
        
        setComparisonData(comparison);
        setWaterfallData(waterfall);
        setSegmentMigrations(segMigrations);
      } catch (error) {
        console.error('Error loading comparison:', error);
      }
    }
  };

  const loadCampaignDetails = async (campaignId) => {
    try {
      const roi = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/campaign-roi/${campaignId}`);
      setSelectedCampaign(roi);
    } catch (error) {
      console.error('Error loading campaign:', error);
    }
  };

  const loadCampaignsSummary = async () => {
    try {
      const summary = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/campaigns-summary`);
      setCampaignsSummary(summary);
    } catch (error) {
      console.error('Error loading campaigns summary:', error);
    }
  };

  const loadCustomerJourney = async (customerKey) => {
    try {
      const journey = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/customer-journey/${customerKey}`);
      setCustomerJourney(journey);
      setSelectedCustomer(customerKey);
    } catch (error) {
      console.error('Error loading journey:', error);
    }
  };

  const loadJourneyPatterns = async () => {
    try {
      const maxVersion = currentVersion || dashboardData?.session?.current_version;
      const url = maxVersion 
        ? `/api/cdp-analytics/sessions/${sessionId}/analytics/journey-patterns?min_version=1&max_version=${maxVersion}`
        : `/api/cdp-analytics/sessions/${sessionId}/analytics/journey-patterns`;
      const patterns = await apiCallJson(url);
      setJourneyPatterns(patterns);
    } catch (error) {
      console.error('Error loading journey patterns:', error);
    }
  };

  const loadJourneySegments = async () => {
    try {
      const version = currentVersion || dashboardData?.session?.current_version || 1;
      const segments = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/journey-segments?version=${version}`);
      setJourneySegments(segments);
    } catch (error) {
      console.error('Error loading journey segments:', error);
    }
  };

  const loadFieldEvolution = async (fieldName) => {
    try {
      const evolution = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/field-evolution/${fieldName}`);
      setFieldEvolution(evolution);
      setSelectedField(fieldName);
    } catch (error) {
      console.error('Error loading field evolution:', error);
    }
  };

  const MetricCard = ({ title, value, change, icon: Icon, trend, subtitle }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center text-sm text-gray-500 mb-2">
            <Icon className="h-4 w-4 mr-2" />
            {title}
          </div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
        </div>
        {change !== undefined && (
          <div className={`flex items-center text-sm font-medium ${
            trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600'
          }`}>
            {trend === 'up' && <ArrowUpRight className="h-4 w-4 mr-1" />}
            {trend === 'down' && <ArrowDownRight className="h-4 w-4 mr-1" />}
            {trend === 'neutral' && <Minus className="h-4 w-4 mr-1" />}
            {change}%
          </div>
        )}
      </div>
    </div>
  );

  const VersionSelector = () => {
    if (!dashboardData?.timeline?.versions) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Version
            </label>
            <select
              value={selectedVersionFrom || ''}
              onChange={(e) => setSelectedVersionFrom(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {dashboardData.timeline.versions.map(v => (
                <option key={v.version_number} value={v.version_number}>
                  V{v.version_number} - {fmt(v.total_customers)} customers
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center justify-center pt-6">
            <ArrowRight className="h-6 w-6 text-gray-400" />
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Version
            </label>
            <select
              value={selectedVersionTo || ''}
              onChange={(e) => setSelectedVersionTo(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {dashboardData.timeline.versions
                .filter(v => !selectedVersionFrom || v.version_number > selectedVersionFrom)
                .map(v => (
                  <option key={v.version_number} value={v.version_number}>
                    V{v.version_number} - {fmt(v.total_customers)} customers
                  </option>
                ))}
            </select>
          </div>
          
          <button
            onClick={loadComparison}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mt-6"
          >
            Compare
          </button>
        </div>
      </div>
    );
  };

  const OverviewMetrics = () => {
    if (!dashboardData) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Customers"
            value={fmt(dashboardData.session.total_customers)}
            icon={Users}
            subtitle={`Version ${dashboardData.session.current_version}`}
          />
          <MetricCard
            title="Total Versions"
            value={dashboardData.session.total_versions}
            icon={Activity}
            subtitle={`${dashboardData.timeline?.versions?.length || 0} data snapshots`}
          />
          {dashboardData.latest_comparison && (
            <>
              <MetricCard
                title="Latest Growth"
                value={fmt(dashboardData.latest_comparison.new_customers)}
                change={dashboardData.latest_comparison.growth_rate}
                trend={dashboardData.latest_comparison.growth_rate > 0 ? 'up' : 'down'}
                icon={TrendingUp}
                subtitle="New customers"
              />
              <MetricCard
                title="Update Rate"
                value={fmt(dashboardData.latest_comparison.updated_customers)}
                change={dashboardData.latest_comparison.update_rate}
                trend="neutral"
                icon={RefreshCw}
                subtitle="Updated records"
              />
            </>
          )}
        </div>

        {/* Ingestion Mode + Source Summary */}
        {dashboardData.session.ingestion_mode && (
          <div className="flex items-center space-x-4 text-sm">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              dashboardData.session.ingestion_mode === 'single_source'
                ? 'bg-green-100 text-green-800'
                : 'bg-blue-100 text-blue-800'
            }`}>
              {dashboardData.session.ingestion_mode === 'single_source' ? 'Single-Source' : 'Multi-Source'}
            </span>
            {dashboardData.session.auto_unify_enabled && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200">
                <CheckCircle className="h-3 w-3 mr-1" />Auto-Unify
              </span>
            )}
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">
              {fmt(dashboardData.session.file_count)} file{dashboardData.session.file_count !== 1 ? 's' : ''}
            </span>
            {dashboardData.session.api_source_count > 0 && (
              <span className="text-gray-600">
                {fmt(dashboardData.session.api_source_count)} API source{dashboardData.session.api_source_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {dashboardData.campaigns && dashboardData.campaigns.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2" />
              Campaign Performance
            </h3>
            <div className="space-y-3">
              {dashboardData.campaigns.slice(0, 5).map((campaign, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                  onClick={() => loadCampaignDetails(campaign.campaign_id)}
                >
                  <div className="flex-1">
                    <div className="font-medium">{campaign.campaign_name}</div>
                    <div className="text-sm text-gray-500">
                      {fmt(campaign.target_customers)} targeted
                    </div>
                  </div>
                  {campaign.roi !== undefined && (
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-sm text-gray-500">ROI</div>
                        <div className={`font-semibold ${campaign.roi_percentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmtSign(campaign.roi_percentage, 1)}%
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Revenue</div>
                        <div className="font-semibold">{fmtMoney(campaign.total_revenue)}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Feature 1: Version-over-Version Comparison with Waterfall
  const VersionComparisonView = () => {
    if (!comparisonData) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Info className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Select versions to compare</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Comparison Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricCard
            title="Starting Base"
            value={fmt(comparisonData.total_customers_before)}
            icon={Users}
            subtitle={`Version ${selectedVersionFrom}`}
          />
          <MetricCard
            title="New Customers"
            value={fmt(comparisonData.new_customers)}
            change={comparisonData.growth_rate}
            trend={comparisonData.growth_rate > 0 ? 'up' : 'down'}
            icon={TrendingUp}
          />
          <MetricCard
            title="Updated"
            value={fmt(comparisonData.updated_customers)}
            change={comparisonData.update_rate}
            trend="neutral"
            icon={RefreshCw}
          />
          <MetricCard
            title="Churned"
            value={fmt(comparisonData.churned_customers)}
            change={comparisonData.churn_rate}
            trend={comparisonData.churn_rate > 0 ? 'down' : 'up'}
            icon={TrendingDown}
          />
        </div>

        {/* Feature 4: Waterfall Diagram */}
        {waterfallData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Customer Flow Waterfall
            </h3>
            <WaterfallChart data={waterfallData} />
          </div>
        )}

        {/* Feature 7: Segment Migrations */}
        {segmentMigrations && segmentMigrations.migrations && segmentMigrations.migrations.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <GitBranch className="h-5 w-5 mr-2" />
              Segment Migrations
            </h3>
            <SegmentMigrationsView data={segmentMigrations} />
          </div>
        )}

        {/* Net Change Summary */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Net Change</div>
              <div className={`text-3xl font-bold ${comparisonData.net_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {fmtSign(comparisonData.net_change)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Final Count: {fmt(comparisonData.total_customers_after)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 mb-1">Growth Rate</div>
              <div className={`text-3xl font-bold ${comparisonData.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {fmtSign(comparisonData.growth_rate, 2)}%
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const convertWaterfallToStages = (waterfallJson) => {
    const map = {
      "Starting Base": "starting_count",
      "New Customers": "new_customers",
      "Updated Customers": "updated_customers",
      "Churned Customers": "churned_customers",
      "Ending Total": "ending_count"
    };

    const result = {
      starting_count: 0,
      new_customers: 0,
      updated_customers: 0,
      churned_customers: 0,
      ending_count: 0
    };

    waterfallJson.waterfall_data.forEach(item => {
      const key = map[item.category];
      if (key) {
        result[key] = item.value;
      }
    });

    return result;
  }

  const WaterfallChart = ({ data }) => {
    data = convertWaterfallToStages(data);
    const stages = [
      { label: 'Starting', value: data.starting_count, color: 'bg-blue-500' },
      { label: 'New', value: data.new_customers, color: 'bg-green-500', isPositive: true },
      { label: 'Update', value: data.updated_customers, color: 'bg-green-500', isPositive: true },
      { label: 'Churned', value: data.churned_customers, color: 'bg-red-500', isNegative: true },
      { label: 'Ending', value: data.ending_count, color: 'bg-blue-600' }
    ];

    const maxValue = Math.max(...stages.map(s => Math.abs(s.value)));

    return (
      <div className="space-y-4">
        {stages.map((stage, idx) => (
          <div key={idx} className="flex items-center space-x-4">
            <div className="w-24 text-sm font-medium text-gray-700">
              {stage.label}
            </div>
            <div className="flex-1 flex items-center space-x-2">
              <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                <div
                  className={`${stage.color} h-full rounded-full transition-all duration-500`}
                  style={{ width: `${(Math.abs(stage.value) / maxValue) * 100}%` }}
                />
              </div>
              <div className={`w-32 text-right font-semibold ${
                stage.isPositive ? 'text-green-600' : 
                stage.isNegative ? 'text-red-600' : 
                'text-gray-900'
              }`}>
                {stage.isPositive && '+'}{stage.isNegative && '-'}
                {fmt(Math.abs(stage.value))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const SegmentMigrationsView = ({ data }) => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Segment
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Retained
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Gained
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Lost
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Retention Rate
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Net Change
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.migrations.map((migration, idx) => {
              const netChange = (migration.customers_gained || 0) - (migration.customers_lost || 0);
              const retentionRate = migration.retention_rate || 0;
              
              return (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {migration.segment_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {fmt(migration.customers_retained)}
                  </td>
                  <td className="px-4 py-3 text-sm text-green-600 font-medium">
                    +{fmt(migration.customers_gained)}
                  </td>
                  <td className="px-4 py-3 text-sm text-red-600 font-medium">
                    -{fmt(migration.customers_lost)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                        <div
                          className={`h-2 rounded-full ${
                            retentionRate >= 80 ? 'bg-green-500' :
                            retentionRate >= 60 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${retentionRate || 0}%` }}
                        />
                      </div>
                      <span className="font-medium">{fmtDec(retentionRate, 1)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`font-semibold ${netChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmtSign(netChange)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Feature 2: Campaign ROI Analysis
  const CampaignROIView = () => {
    const summaryLoadedRef = useRef(false);

    useEffect(() => {
      if (activeView === 'campaigns' && !summaryLoadedRef.current && !campaignsSummary) {
        summaryLoadedRef.current = true;
        loadCampaignsSummary().catch(() => {
          summaryLoadedRef.current = false; // Reset on error to allow retry
        });
      }
    }, [activeView, campaignsSummary]);

    if (!dashboardData?.campaigns && !campaignsSummary) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No campaigns found</p>
        </div>
      );
    }

    const campaigns = campaignsSummary?.campaigns || dashboardData?.campaigns || [];

    return (
      <div className="space-y-6">
        {/* Campaign Summary Cards */}
        {campaignsSummary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Total Campaigns"
              value={campaignsSummary.total_campaigns}
              icon={Target}
            />
            <MetricCard
              title="Total Revenue"
              value={'$' + fmt(campaignsSummary.total_revenue)}
              icon={DollarSign}
              trend="up"
            />
            <MetricCard
              title="Total Cost"
              value={'$' + fmt(campaignsSummary.total_cost)}
              icon={DollarSign}
              trend="down"
            />
            <MetricCard
              title="Average ROI"
              value={fmtPct(campaignsSummary.average_roi, 1)}
              change={campaignsSummary.average_roi}
              trend={campaignsSummary.average_roi > 0 ? 'up' : 'down'}
              icon={TrendingUp}
            />
          </div>
        )}

        {/* Campaign List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2" />
            Campaign ROI Analysis
          </h3>
          <div className="space-y-3">
            {campaigns.map((campaign, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                onClick={() => loadCampaignDetails(campaign.campaign_id)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{campaign.campaign_name}</h4>
                    <p className="text-sm text-gray-500">
                      {fmt(campaign.target_customers)} targeted
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    campaign.roi_percentage > 50 ? 'bg-green-100 text-green-800' :
                    campaign.roi_percentage > 0 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {fmtSign(campaign.roi_percentage, 1)}% ROI
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Conversions</div>
                    <div className="font-semibold">{fmt(campaign.conversion_count)}</div>
                    <div className="text-xs text-gray-500">{fmtDec(campaign.conversion_rate, 1)}% rate</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Revenue</div>
                    <div className="font-semibold text-green-600">
                      ${fmt(campaign.total_revenue)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Cost</div>
                    <div className="font-semibold text-red-600">
                      ${fmt(campaign.campaign_cost)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">CPA</div>
                    <div className="font-semibold">
                      ${fmtDec(campaign.cost_per_acquisition, 2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Avg Revenue</div>
                    <div className="font-semibold">
                      ${fmtDec(campaign.avg_revenue_per_customer, 2)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Campaign Detail */}
        {selectedCampaign && (
          <div className="bg-white rounded-lg shadow-sm border-2 border-blue-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Campaign Deep Dive</h3>
              <button
                onClick={() => setSelectedCampaign(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-lg mb-2">{selectedCampaign.campaign_name}</h4>
                <p className="text-sm text-gray-500">
                  Campaign ID: {selectedCampaign.campaign_id}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-sm text-gray-500 mb-1">Targeted</div>
                  <div className="text-2xl font-bold">{fmt(selectedCampaign.target_customers)}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-sm text-gray-500 mb-1">Conversions</div>
                  <div className="text-2xl font-bold text-green-600">{fmt(selectedCampaign.conversion_count)}</div>
                  <div className="text-xs text-gray-500 mt-1">{fmtDec(selectedCampaign.conversion_rate, 2)}%</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-sm text-gray-500 mb-1">Avg Revenue/Customer</div>
                  <div className="text-2xl font-bold">{fmtMoney(selectedCampaign.avg_revenue_per_customer, 2)}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-sm text-gray-500 mb-1">Cost per Acquisition</div>
                  <div className="text-2xl font-bold">{fmtMoney(selectedCampaign.cost_per_acquisition, 2)}</div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
                    <div className="text-2xl font-bold text-green-600">
                      ${fmt(selectedCampaign.total_revenue)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Total Cost</div>
                    <div className="text-2xl font-bold text-red-600">
                      ${fmt(selectedCampaign.campaign_cost)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Net ROI</div>
                    <div className={`text-3xl font-bold ${selectedCampaign.roi > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(selectedCampaign.roi ?? 0) >= 0 ? '+' : ''}${fmt(selectedCampaign.roi)}
                    </div>
                    <div className={`text-lg font-semibold mt-1 ${selectedCampaign.roi_percentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmtSign(selectedCampaign.roi_percentage, 1)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Feature 3: Customer Journey Tracking
  const JourneyTrackingView = () => {
    const patternsLoadedRef = useRef(false);

    useEffect(() => {
      if (activeView === 'journeys' && !patternsLoadedRef.current && !journeyPatterns) {
        patternsLoadedRef.current = true;
        Promise.all([
          loadJourneyPatterns(),
          loadJourneySegments()
        ]).catch(() => {
          patternsLoadedRef.current = false; // Reset on error
        });
      }
    }, [activeView, journeyPatterns]);

    return (
      <div className="space-y-6">
        {/* Customer Journey Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <MapPin className="h-5 w-5 mr-2" />
            Search Customer Journey
          </h3>
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Enter customer key..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && customerSearch) {
                    loadCustomerJourney(customerSearch);
                  }
                }}
              />
            </div>
            <button
              onClick={() => customerSearch && loadCustomerJourney(customerSearch)}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </div>

        {/* Customer Journey Detail */}
        {customerJourney && (
          <div className="bg-white rounded-lg shadow-sm border-2 border-blue-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Customer Journey: {selectedCustomer}</h3>
              <button
                onClick={() => {
                  setCustomerJourney(null);
                  setSelectedCustomer(null);
                  setCustomerSearch('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">First Seen</div>
                <div className="text-lg font-bold">V{customerJourney.first_seen_version}</div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Last Seen</div>
                <div className="text-lg font-bold">V{customerJourney.last_seen_version}</div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Conversions</div>
                <div className="text-lg font-bold text-green-600">{customerJourney.conversion_count}</div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Total Value</div>
                <div className="text-lg font-bold text-green-600">{fmtMoney(customerJourney.total_value, 2)}</div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3">Journey Timeline</h4>
              <div className="space-y-3">
                {customerJourney.journey_events?.map((event, idx) => (
                  <div key={idx} className="relative">
                    {idx < customerJourney.journey_events.length - 1 && (
                      <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" />
                    )}
                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold z-10 ${
                        event.event_type === 'new' ? 'bg-blue-500' :
                        event.event_type === 'updated' ? 'bg-yellow-500' :
                        event.event_type === 'conversion' ? 'bg-green-500' :
                        'bg-gray-500'
                      }`}>
                        {event.version || 'C'}
                      </div>
                      <div className="flex-1 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium capitalize">{event.event_type}</div>
                          <div className="text-sm text-gray-500">
                            {event.timestamp && new Date(event.timestamp).toLocaleString()}
                          </div>
                        </div>
                        {event.campaign_id && (
                          <div className="text-sm text-gray-600">
                            Campaign: {event.campaign_id}
                          </div>
                        )}
                        {event.conversion_value && (
                          <div className="text-sm font-semibold text-green-600 mt-1">
                            +${fmtDec(event.conversion_value, 2)}
                          </div>
                        )}
                        {event.changed_fields && event.changed_fields.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Updated: {event.changed_fields.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Journey Patterns */}
        {journeyPatterns && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Common Journey Patterns
            </h3>
            <div className="space-y-3">
              {journeyPatterns.patterns?.map((pattern, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-mono text-sm font-medium mb-1">
                        {pattern.pattern}
                      </div>
                      <div className="text-xs text-gray-500">
                        {fmt(pattern.count)} customers ({fmtDec(pattern.percentage, 1)}%)
                      </div>
                    </div>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${pattern.percentage || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Journey Segments */}
        {journeySegments && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Layers className="h-5 w-5 mr-2" />
              Journey Segments
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {journeySegments.segments?.map((segment, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="font-medium mb-2">{segment.segment_name}</div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">
                    {fmt(segment.customer_count)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Avg Journey Length: {fmtDec(segment.avg_journey_length, 1)} steps
                  </div>
                  {segment.avg_value && (
                    <div className="text-sm font-semibold text-green-600 mt-2">
                      Avg Value: {fmtMoney(segment.avg_value, 2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Feature 5: Cohort Analysis
  const CohortAnalysisView = () => {
    if (!dashboardData?.cohorts) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No cohort data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Cohort Retention by Acquisition Version
          </h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Cohort
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Cohort Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Current Retention
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Retention Trend
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.cohorts.map((cohort, idx) => {
                  const retentionValues = Object.values(cohort.retention_rates || {});
                  const currentRetention = cohort.retention_current || retentionValues[retentionValues.length - 1] || 0;
                  
                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {cohort.cohort_identifier || cohort.cohort_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {fmt(cohort.cohort_size)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-semibold ${
                          currentRetention > 80 ? 'text-green-600' :
                          currentRetention > 50 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {fmtDec(currentRetention, 1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1">
                          {retentionValues.slice(0, 8).map((rate, vIdx) => (
                            <div
                              key={vIdx}
                              className="relative group"
                              title={`Version ${vIdx + (cohort.cohort_version || cohort.acquisition_version || 1)}: {fmtMoney(rate, 1)}%`}
                            >
                              <div
                                className={`h-8 w-8 rounded ${
                                  rate > 80 ? 'bg-green-500' :
                                  rate > 50 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ opacity: rate / 100 }}
                              />
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cohort Performance Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">High Retention Cohorts</div>
            <div className="text-2xl font-bold text-green-600">
              {dashboardData.cohorts.filter(c => (c.retention_current || 0) > 80).length}
            </div>
            <div className="text-xs text-gray-500 mt-1">&gt;80% retention</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Medium Retention Cohorts</div>
            <div className="text-2xl font-bold text-yellow-600">
              {dashboardData.cohorts.filter(c => {
                const ret = c.retention_current || 0;
                return ret >= 50 && ret <= 80;
              }).length}
            </div>
            <div className="text-xs text-gray-500 mt-1">50-80% retention</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">At-Risk Cohorts</div>
            <div className="text-2xl font-bold text-red-600">
              {dashboardData.cohorts.filter(c => (c.retention_current || 0) < 50).length}
            </div>
            <div className="text-xs text-gray-500 mt-1">&lt;50% retention</div>
          </div>
        </div>
      </div>
    );
  };

  // Feature 6: Field Evolution
  const FieldEvolutionView = () => {
    const [availableFields, setAvailableFields] = useState([]);
    const fieldsLoadedRef = useRef(false);

    useEffect(() => {
      if (activeView === 'field-evolution' && !fieldsLoadedRef.current) {
        fieldsLoadedRef.current = true;
        // Load available fields from API
        apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/available-fields`)
          .then(response => {
            setAvailableFields(response.fields || []);
          })
          .catch(error => {
            console.error('Error loading fields:', error);
            fieldsLoadedRef.current = false;
            // Fallback to default fields
            setAvailableFields(['email', 'phone', 'revenue', 'ltv', 'status', 'city', 'age']);
          });
      }
    }, [activeView]);

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Zap className="h-5 w-5 mr-2" />
            Field Evolution Tracking
          </h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Field to Analyze
            </label>
            <select
              value={selectedField || ''}
              onChange={(e) => e.target.value && loadFieldEvolution(e.target.value)}
              className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Choose a field...</option>
              {availableFields.map(field => (
                <option key={field} value={field}>{field}</option>
              ))}
            </select>
          </div>
        </div>

        {fieldEvolution && (
          <div className="bg-white rounded-lg shadow-sm border-2 border-blue-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Evolution: {fieldEvolution.field_name}
              </h3>
              <button
                onClick={() => {
                  setFieldEvolution(null);
                  setSelectedField(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Version
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Null %
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Distinct Values
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Top Values
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Numeric Stats
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fieldEvolution.evolution?.map((ev, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">V{ev.version}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center">
                          <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className={`h-2 rounded-full ${
                                ev.null_percentage < 10 ? 'bg-green-500' :
                                ev.null_percentage < 30 ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${ev.null_percentage || 0}%` }}
                            />
                          </div>
                          {fmtDec(ev.null_percentage, 1)}%
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{fmt(ev.distinct_values)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-xs text-gray-600 max-w-xs truncate">
                          {ev.top_values?.slice(0, 3).join(', ')}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {ev.numeric_stats ? (
                          <div className="text-xs text-gray-600">
                            <div>Avg: {fmtDec(ev.numeric_stats.average, 2)}</div>
                            <div>Min: {fmtDec(ev.numeric_stats.minimum, 2)}</div>
                            <div>Max: {fmtDec(ev.numeric_stats.maximum, 2)}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Field Evolution Chart */}
            <div className="mt-6">
              <h4 className="font-semibold mb-3">Quality Trend</h4>
              <div className="space-y-2">
                {fieldEvolution.evolution?.map((ev, idx) => (
                  <div key={idx} className="flex items-center space-x-4">
                    <div className="w-16 text-sm font-medium text-gray-700">
                      V{ev.version}
                    </div>
                    <div className="flex-1 flex items-center space-x-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                        <div
                          className="bg-green-500 h-full rounded-full transition-all"
                          style={{ width: `${100 - (ev.null_percentage || 0)}%` }}
                        />
                      </div>
                      <div className="w-24 text-right text-sm font-semibold text-green-600">
                        {fmtDec(100 - (ev.null_percentage ?? 0), 1)}% filled
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const TimelineView = () => {
    if (!dashboardData?.timeline?.versions) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Calendar className="h-5 w-5 mr-2" />
          Version Timeline
        </h3>

        <div className="space-y-4">
          {dashboardData.timeline.versions.map((version, idx) => (
            <div key={idx} className="relative">
              {idx < dashboardData.timeline.versions.length - 1 && (
                <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gray-200" />
              )}
              
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  version.upload_type === 'initial' ? 'bg-blue-500' :
                  version.upload_type === 'addendum' ? 'bg-green-500' :
                  'bg-purple-500'
                } text-white font-semibold text-sm z-10`}>
                  {version.version_number}
                </div>

                <div className="flex-1 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-semibold">
                        Version {version.version_number}
                        {version.campaign_name && ` - ${version.campaign_name}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        {version.created_at && new Date(version.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      version.upload_type === 'initial' ? 'bg-blue-100 text-blue-800' :
                      version.upload_type === 'addendum' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {version.upload_type}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-gray-500">Total Customers</div>
                      <div className="font-semibold">{fmt(version.total_customers)}</div>
                    </div>
                    {version.new_customers !== undefined && (
                      <div>
                        <div className="text-gray-500">New</div>
                        <div className="font-semibold text-green-600">+{fmt(version.new_customers)}</div>
                      </div>
                    )}
                    {version.updated_customers !== undefined && (
                      <div>
                        <div className="text-gray-500">Updated</div>
                        <div className="font-semibold text-blue-600">{fmt(version.updated_customers)}</div>
                      </div>
                    )}
                    {version.growth_rate !== undefined && (
                      <div>
                        <div className="text-gray-500">Growth</div>
                        <div className={`font-semibold ${version.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmtSign(version.growth_rate, 2)}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No analytics data available</p>
        <button
          onClick={loadDashboard}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // ==================== Data Sources View ====================
  
  const DataSourcesView = () => {
    const [sourcesData, setSourcesData] = useState(null);
    const [sourcesLoading, setSourcesLoading] = useState(false);
    
    useEffect(() => {
      if (activeView === 'data-sources' && !sourcesData) {
        loadSourcesData();
      }
    }, [activeView]);
    
    const loadSourcesData = async () => {
      setSourcesLoading(true);
      try {
        const data = await apiCallJson(
          `/api/cdp-analytics/sessions/${sessionId}/analytics/data-sources`
        );
        setSourcesData(data);
      } catch (e) {
        console.error('Error loading data sources:', e);
      } finally {
        setSourcesLoading(false);
      }
    };
    
    if (sourcesLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-gray-500">Loading source analytics...</span>
        </div>
      );
    }
    
    if (!sourcesData) return null;
    
    const { summary, file_sources, api_sources, recent_merges, activity_timeline } = sourcesData;
    
    return (
      <div className="space-y-6">
        {/* Ingestion Mode Banner */}
        <div className={`p-4 rounded-lg border ${
          sourcesData.ingestion_mode === 'single_source'
            ? 'bg-green-50 border-green-200'
            : sourcesData.ingestion_mode === 'multi_source'
            ? 'bg-blue-50 border-blue-200'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {sourcesData.ingestion_mode === 'single_source' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : sourcesData.ingestion_mode === 'multi_source' ? (
                <Layers className="h-5 w-5 text-blue-600" />
              ) : (
                <Info className="h-5 w-5 text-gray-500" />
              )}
              <div>
                <div className="font-semibold text-sm">
                  {sourcesData.ingestion_mode === 'single_source'
                    ? 'Single-Source Mode — Homogeneous Schema'
                    : sourcesData.ingestion_mode === 'multi_source'
                    ? 'Multi-Source Mode — Heterogeneous Schemas'
                    : 'Ingestion Mode Not Yet Determined'}
                </div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {sourcesData.ingestion_mode === 'single_source'
                    ? `All files share the same schema. Auto-unify is ${sourcesData.auto_unify_enabled ? 'enabled' : 'available'}.`
                    : sourcesData.ingestion_mode === 'multi_source'
                    ? 'Files have different columns — manual column mapping required during unification.'
                    : 'Upload a second file to auto-detect whether schemas match.'}
                </div>
              </div>
            </div>
            {sourcesData.base_schema_hash && (
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <Hash className="h-3 w-3" />
                <span className="font-mono">{sourcesData.base_schema_hash}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <FileText className="h-4 w-4 mr-2" />File Sources
            </div>
            <div className="text-2xl font-bold">{fmt(summary.file_sources_count)}</div>
            <div className="text-xs text-gray-400 mt-1">{fmtBytes(summary.total_file_size_bytes)} total</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <Globe className="h-4 w-4 mr-2" />API Sources
            </div>
            <div className="text-2xl font-bold">{fmt(summary.api_sources_count)}</div>
            <div className="text-xs text-gray-400 mt-1">
              {summary.total_api_pending > 0
                ? <span className="text-orange-600">{fmt(summary.total_api_pending)} pending</span>
                : 'All merged'}
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <Upload className="h-4 w-4 mr-2" />Records from Files
            </div>
            <div className="text-2xl font-bold">{fmt(summary.total_records_from_files)}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <Activity className="h-4 w-4 mr-2" />Records from APIs
            </div>
            <div className="text-2xl font-bold">{fmt(summary.total_api_merged)}</div>
            <div className="text-xs text-gray-400 mt-1">{fmt(summary.total_api_received)} received total</div>
          </div>
        </div>
        
        {/* Schema Compatibility */}
        {summary.file_sources_count > 1 && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Schema Compatibility</h3>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm">{fmt(summary.schema_matches)} matching</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-sm">{fmt(summary.schema_mismatches)} different</span>
              </div>
              <div className="flex-1">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${(summary.schema_matches / Math.max(summary.file_sources_count, 1)) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* File Sources Table */}
        {file_sources.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                <FileText className="h-4 w-4 mr-2" />File Sources ({file_sources.length})
              </h3>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rows</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cols</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Schema</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {file_sources.map((f) => (
                  <tr key={f.source_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 truncate max-w-[200px]">{f.name}</td>
                    <td className="px-4 py-2 text-xs text-gray-500 uppercase">{f.file_type}</td>
                    <td className="px-4 py-2 text-sm text-right">{fmt(f.records)}</td>
                    <td className="px-4 py-2 text-sm text-right">{fmt(f.columns)}</td>
                    <td className="px-4 py-2 text-sm text-right text-gray-500">{fmtBytes(f.file_size)}</td>
                    <td className="px-4 py-2 text-center">
                      {f.schema_match === true ? (
                        <CheckCircle className="h-4 w-4 text-green-500 inline" />
                      ) : f.schema_match === false ? (
                        <AlertTriangle className="h-4 w-4 text-red-500 inline" />
                      ) : (
                        <span className="text-xs text-gray-400">Base</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* API Sources Table */}
        {api_sources.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                <Globe className="h-4 w-4 mr-2" />API Sources ({api_sources.length})
              </h3>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Mode</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Received</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Merged</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {api_sources.map((a) => (
                  <tr key={a.source_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{a.name}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.status === 'active' ? 'bg-green-100 text-green-800' :
                        a.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>{a.status}</span>
                    </td>
                    <td className="px-4 py-2 text-center text-xs text-gray-500">{a.merge_mode}</td>
                    <td className="px-4 py-2 text-sm text-right">{fmt(a.records_received)}</td>
                    <td className="px-4 py-2 text-sm text-right text-green-600">{fmt(a.records_merged)}</td>
                    <td className="px-4 py-2 text-sm text-right">
                      {a.pending_records > 0 ? (
                        <span className="text-orange-600 font-medium">{fmt(a.pending_records)}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {a.last_received_at
                        ? new Date(a.last_received_at).toLocaleString()
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Recent Merge Jobs */}
        {recent_merges.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                <GitBranch className="h-4 w-4 mr-2" />Recent Merge Jobs
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {recent_merges.slice(0, 10).map((m) => (
                <div key={m.job_id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      m.status === 'completed' ? 'bg-green-500' :
                      m.status === 'running' ? 'bg-blue-500 animate-pulse' :
                      m.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'
                    }`} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{m.source_name}</div>
                      <div className="text-xs text-gray-500">
                        {m.trigger_type} • {fmt(m.records_processed)} records
                        {m.duration_ms ? ` • ${fmt(m.duration_ms)}ms` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 flex-shrink-0 ml-4">
                    {m.started_at ? new Date(m.started_at).toLocaleString() : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Activity Timeline */}
        {activity_timeline.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                <Clock className="h-4 w-4 mr-2" />Source Activity Timeline
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {activity_timeline.map((a, idx) => (
                <div key={idx} className="px-4 py-2.5 flex items-center space-x-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    a.source_type === 'file' ? 'bg-blue-100' :
                    a.source_type === 'api' ? 'bg-purple-100' : 'bg-gray-100'
                  }`}>
                    {a.source_type === 'file' ? (
                      <FileText className="h-3 w-3 text-blue-600" />
                    ) : a.source_type === 'api' ? (
                      <Globe className="h-3 w-3 text-purple-600" />
                    ) : (
                      <Target className="h-3 w-3 text-gray-600" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">{a.source_name}</span>
                      <span className="text-gray-500 ml-1">— {a.action}</span>
                    </div>
                    <div className="text-xs text-gray-400">
                      {fmt(a.records)} records
                      {a.schema_match === true && ' • Schema ✓'}
                      {a.schema_match === false && ' • Schema ✗'}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      a.status === 'completed' ? 'bg-green-500' :
                      a.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'
                    }`} />
                    <span className="text-xs text-gray-400">
                      {a.timestamp ? new Date(a.timestamp).toLocaleString() : '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Empty state */}
        {file_sources.length === 0 && api_sources.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No data sources yet. Upload files or create API sources to get started.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap space-x-2 px-6" aria-label="Tabs">
            {[
              { id: 'overview', label: 'Overview', icon: TrendingUp },
              { id: 'data-sources', label: 'Data Sources', icon: Database },
              { id: 'comparison', label: 'Version Comparison', icon: BarChart3 },
              { id: 'campaigns', label: 'Campaign ROI', icon: Target },
              { id: 'journeys', label: 'Customer Journeys', icon: MapPin },
              { id: 'cohorts', label: 'Cohort Analysis', icon: Users },
              { id: 'field-evolution', label: 'Field Evolution', icon: Zap },
              { id: 'timeline', label: 'Timeline', icon: Calendar }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={`py-4 px-3 border-b-2 font-medium text-sm flex items-center ${
                  activeView === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeView === 'overview' && <OverviewMetrics />}
          {activeView === 'data-sources' && <DataSourcesView />}
          {activeView === 'comparison' && (
            <>
              <VersionSelector />
              <VersionComparisonView />
            </>
          )}
          {activeView === 'campaigns' && <CampaignROIView />}
          {activeView === 'journeys' && <JourneyTrackingView />}
          {activeView === 'cohorts' && <CohortAnalysisView />}
          {activeView === 'field-evolution' && <FieldEvolutionView />}
          {activeView === 'timeline' && <TimelineView />}
        </div>
      </div>
    </div>
  );
};

export default CDPAnalyticsTab;